import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
    getCachedBillingData,
    getBillingData,
    getForceUpgrade,
    getCustomerBalance,
} from '../data/api';
import * as Sentry from '@sentry/react';
import { envToBoolean } from '../utils/env';
import { sleep } from '../utils/common';
import PlanDetails from './Dashboard/PlanDetails';
import Invoices from './Dashboard/Invoices';
import BillingModeModal from './Dashboard/BillingModeModal';
import CancelModal from './shared/CancelModal';
import InvoiceDetailsModal from './shared/InvoiceDetailsModal';
import CardFields from './stripe/CardFields';
import BillingContactModal from './Dashboard/BillingContactModal';
import Loading from './shared/Loading';
import { ROUTES, WEBHOOK_PROCESSING_DELAY_MS } from '../utils/constants';
import getForceUpgradeStatus, { FORCE_UPGRADE_STATUS } from '../utils/get-force-upgrade-status';

let _isMounted = false;

/**
 * @typedef {import('../types').BillingData} BillingData
 * @typedef {import('../utils/get-force-upgrade-status').ForceUpgradeStatus} ForceUpgradeStatus
 * @typedef {object} DashboardProps
 * @property {{push: (route: string) => void}} history
 * @property {(title: string) => void} updateTitle
 * @property {(visible: boolean) => void} showNav
 */

/**
 * @param {ForceUpgradeStatus} status
 * @returns {boolean}
 */
function isNonOwnerForceUpgradeStatus(status) {
    return status === FORCE_UPGRADE_STATUS.NON_OWNER;
}

/**
 * @param {object} props
 * @param {{name?: string|null, email?: string|null}|null|undefined} props.siteOwner
 * @returns {React.ReactNode}
 */
function NonOwnerForceUpgradeNotice({ siteOwner }) {
    const reachOutTo = siteOwner?.email
        ? siteOwner?.name
            ? `${siteOwner.name} | ${siteOwner.email}`
            : siteOwner.email
        : 'the site owner';

    return (
        <section
            className="gh-loading-content gh-force-upgrade-notice"
            role="status"
            aria-labelledby="force-upgrade-notice-title"
            aria-describedby="force-upgrade-notice-description"
        >
            <div className="gh-force-upgrade-notice-content">
                <h1 id="force-upgrade-notice-title">Ghost Admin is locked</h1>
                <p id="force-upgrade-notice-description">
                    The trial for this site has ended. Ask {reachOutTo} to subscribe to a paid plan
                    to restore access.
                </p>
            </div>
        </section>
    );
}

/**
 * Renders billing recovery and account-management actions for the current user.
 * @param {DashboardProps} props
 * @returns {React.ReactNode}
 */
function Dashboard({ history, updateTitle, showNav }) {
    const _verbose = envToBoolean(process.env.REACT_APP_VERBOSE);

    const [loading, setLoading] = useState(true);
    const [billingData, setBillingData] = useState(null);
    const [noCurrentPrice, setNoCurrentPrice] = useState(false);
    const [canHaveAutoBilling, setCanHaveAutoBilling] = useState(false);
    const [autoBillingEnabled, setAutoBillingEnabled] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);
    const [billingModeModalIsOpen, setBillingModeModalIsOpen] = useState(false);
    const [invoiceDetailsModalIsOpen, setInvoiceDetailsModalIsOpen] = useState(false);
    const [billingContactModalIsOpen, setBillingContactModalIsOpen] = useState(false);
    const [customerBalance, setCustomerBalance] = useState(null);

    useEffect(() => {
        showNav(false);

        const fetchCachedBillingData = async () => {
            // Arriving from a successful payment on /update-card: hold the
            // regular loading state while the Stripe → Daisy webhooks settle,
            // then fetch fresh state (reporting it to Ghost Admin) so the
            // overview never paints the stale grace warnings after a payment.
            if (history.location?.state?.finalizePayment) {
                // Clear the flag first so a reload doesn't wait again
                history.replace(ROUTES.DASHBOARD);
                await sleep(WEBHOOK_PROCESSING_DELAY_MS);
                await getBillingData({ updateAdmin: true });
            }

            /** @type {BillingData} */
            const cachedBillingData = await getCachedBillingData();

            Sentry.setTags({
                source: 'dashboard',
            });

            setBillingData(cachedBillingData);
            const forceUpgradeStatus = getForceUpgradeStatus(cachedBillingData);

            if (isNonOwnerForceUpgradeStatus(forceUpgradeStatus)) {
                setLoading(false);
                return;
            }

            // Handle different states of errors or no permissions:
            //  - Legacy plans or multiple sites per user will resolve in a 403 status
            //  - Any other error tells the user to contact support.
            if (
                !cachedBillingData ||
                cachedBillingData?.error ||
                !cachedBillingData?.availableProducts
            ) {
                setShowError(true);

                if (cachedBillingData?.error?.status === 403) {
                    setErrorMessage(
                        <>
                            You're on a special legacy plan. If you need to make any billing or
                            domain changes, reach out to us on{' '}
                            <a href="mailto:support@ghost.org" onClick={contactSupport}>
                                support@ghost.org
                            </a>
                        </>,
                    );
                } else if (cachedBillingData?.error?.status === 408) {
                    setErrorMessage(
                        <>
                            The request timed out. Try again or contact{' '}
                            <a href="mailto:support@ghost.org" onClick={contactSupport}>
                                support@ghost.org
                            </a>
                            .
                        </>,
                    );
                } else {
                    setErrorMessage(
                        <>
                            Contact{' '}
                            <a href="mailto:support@ghost.org" onClick={contactSupport}>
                                support@ghost.org
                            </a>{' '}
                            for changes to your domain or billing.
                        </>,
                    );
                }
            }

            if (forceUpgradeStatus === FORCE_UPGRADE_STATUS.STALE_OWNER) {
                const refreshedBillingData = await getForceUpgrade();
                if (refreshedBillingData) {
                    setBillingData(refreshedBillingData);
                }
            }

            if (forceUpgradeStatus === FORCE_UPGRADE_STATUS.LAPSED_TRIAL_OWNER) {
                return history.push(ROUTES.PLANS);
            } else if (cachedBillingData?.isIncomplete || cachedBillingData?.isAutoBillingGrace) {
                // Redirect back to Checkout with the current plan to finish the payment
                return history.push(cachedBillingData.checkoutRoute);
            }

            const hasAutoBillingEnabled = cachedBillingData.hasAutoBillingEnabled;
            const autoBillingAddonAvailable = cachedBillingData?.autoPriceId;

            const autoBillingAllowed = Boolean(
                autoBillingAddonAvailable &&
                !!cachedBillingData?.autoPriceId &&
                !cachedBillingData?.isForceUpgrade &&
                (hasAutoBillingEnabled || cachedBillingData?.user?.show_auto_billing),
            );

            if (cachedBillingData?.isTrial && !cachedBillingData?.error) {
                return history.push(ROUTES.PLANS);
            }

            const stripeCustomerId =
                cachedBillingData?.subscription?.customer_id ||
                cachedBillingData?.user?.stripe_token;

            if (stripeCustomerId) {
                const customerBalanceResponse = await getCustomerBalance({
                    customerId: stripeCustomerId,
                });

                if (_verbose) {
                    console.log(
                        '[BMA] Customer balance:',
                        customerBalanceResponse?.balance,
                        'Error:',
                        customerBalanceResponse?.error,
                    );
                }

                if (customerBalanceResponse?.balance < 0 && !customerBalanceResponse?.error) {
                    setCustomerBalance(customerBalanceResponse?.balance);
                }
            }

            setLoading(false);

            setNoCurrentPrice(!cachedBillingData?.currentPrice?.stripe_price_id);
            setAutoBillingEnabled(hasAutoBillingEnabled);
            setCanHaveAutoBilling(autoBillingAllowed);
            setInvoiceDetails(cachedBillingData?.invoiceDetails);
        };

        if (!_isMounted) {
            _isMounted = true;

            updateTitle('Billing and domain settings');
            fetchCachedBillingData();
        }
        return () => {
            _isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (typeof showNav === 'function') {
            if (billingData?.error || billingData?.isInactive || noCurrentPrice) {
                showNav(false);
            } else {
                showNav(true);
            }
        }
    }, [showNav, billingData, noCurrentPrice]);

    useEffect(() => {
        // Update the auto billing state if it changed
        if (billingData?.hasAutoBillingEnabled !== autoBillingEnabled) {
            setAutoBillingEnabled(billingData?.hasAutoBillingEnabled);
        }
    }, [billingData?.hasAutoBillingEnabled, autoBillingEnabled]);

    // Users on an active trial don't have access to the Dashboard and get
    // redirected to the plan selection instead
    useEffect(() => {
        if (billingData?.isTrial && !billingData?.error) {
            return history.push(ROUTES.PLANS);
        }
    }, [billingData?.isTrial, billingData?.error, history]);

    /**
     * Close all modals and reload the billing data
     * @param {{reload?: boolean, delay?: number}} options
     * @returns {Promise<void>}
     */
    const handleCloseModalsAndReload = async (options = {}) => {
        const reload = options?.reload || false;
        const delay = options?.delay || 0;

        setCancelModalIsOpen(false);
        setBillingModeModalIsOpen(false);
        setInvoiceDetailsModalIsOpen(false);
        setBillingContactModalIsOpen(false);

        if (reload && reload === true) {
            showNav(false);

            setLoading(true);

            // Wait for a bit before fetching the updated subscription data.
            // This should give Stripe and Daisy enough time to process webhooks
            // and receive the updated subscription state after a customer was in
            // dunning.
            await sleep(delay);

            const updatedBillingData = await getBillingData({
                updateAdmin: delay > 0 ? true : false,
            });

            setBillingData(updatedBillingData);
            setLoading(false);
        } else {
            const cachedBillingData = await getCachedBillingData();

            await sleep(delay);

            setBillingData(cachedBillingData);
            setInvoiceDetails(cachedBillingData?.invoiceDetails);
        }

        showNav(true);
        return;
    };

    const handleNestedLoadingState = (show) => {
        setLoading(show);
        showNav(!show);
    };

    const contactSupport = (event) => {
        event.preventDefault();
        return (window.location.href = 'mailto:support@ghost.org');
    };

    const forceUpgradeStatus = getForceUpgradeStatus(billingData);

    return (
        <main className="main">
            {loading ? (
                <Loading />
            ) : isNonOwnerForceUpgradeStatus(forceUpgradeStatus) ? (
                <NonOwnerForceUpgradeNotice siteOwner={billingData?.siteOwner} />
            ) : showError ? (
                <p style={{ textAlign: 'center', gridColumn: '1 / 3' }}>{errorMessage}</p>
            ) : (
                <div className="accountgrid" data-test-id="dashboard">
                    <PlanDetails
                        currentSiteLimits={billingData?.currentSiteLimits}
                        currentPrice={billingData?.currentPrice}
                        subscriptionMeta={billingData?.subscription?.meta}
                        pendingSubscription={billingData?.pendingSubscription}
                        isPending={billingData?.isPending}
                        checkoutRoute={billingData?.checkoutRoute}
                        isGrace={billingData?.isGrace}
                        discount={billingData?.discount}
                        customerBalance={customerBalance}
                        currentPeriodEnd={billingData?.subscription?.current_period_end}
                        willCancelAtPeriodEnd={billingData?.willCancelAtPeriodEnd}
                        closeAndReload={handleCloseModalsAndReload}
                        subscriptionId={billingData?.subscription?.id}
                        customerId={
                            billingData?.subscription?.customer_id ||
                            billingData?.user?.stripe_token
                        }
                    />

                    <div className="box-carddetails box-wrap">
                        <div className="box-label">Payment information</div>
                        <div className="box">
                            <section className="staticfield">
                                <header>
                                    <div className="staticfield-label">Credit card</div>
                                    <button
                                        className="staticfield-link"
                                        data-test-btn="open-card"
                                        onClick={() => history.push(ROUTES.UPDATE_CARD)}
                                    >
                                        {billingData?.isGrace ? 'Update' : 'Edit'}
                                    </button>
                                </header>

                                <CardFields user={billingData?.user} />
                            </section>

                            {canHaveAutoBilling && (
                                <BillingModeModal
                                    user={billingData?.user}
                                    subscription={billingData?.subscription}
                                    autoPriceId={billingData?.autoPriceId}
                                    handleCloseModal={handleCloseModalsAndReload}
                                    exceededLimits={billingData?.exceededLimits}
                                    autoBillingEnabled={autoBillingEnabled}
                                    setAutoBillingEnabled={setAutoBillingEnabled}
                                    setModalIsOpen={setBillingModeModalIsOpen}
                                    modalIsOpen={billingModeModalIsOpen}
                                    willCancelAtPeriodEnd={billingData?.willCancelAtPeriodEnd}
                                    isGrace={billingData?.isGrace}
                                />
                            )}

                            <InvoiceDetailsModal
                                handleCloseModal={handleCloseModalsAndReload}
                                invoiceDetails={invoiceDetails}
                                customerId={
                                    billingData?.subscription?.customer_id ||
                                    billingData?.user?.stripe_token
                                }
                                setModalIsOpen={setInvoiceDetailsModalIsOpen}
                                modalIsOpen={invoiceDetailsModalIsOpen}
                            />

                            <BillingContactModal
                                user={billingData?.user}
                                setModalIsOpen={setBillingContactModalIsOpen}
                                modalIsOpen={billingContactModalIsOpen}
                                handleCloseModal={handleCloseModalsAndReload}
                            />
                        </div>
                    </div>

                    {billingData?.invoices && <Invoices invoices={billingData?.invoices} />}

                    <footer className="footer">
                        {!billingData?.willCancelAtPeriodEnd &&
                            (billingData?.isActive || billingData?.isGrace) && (
                                <CancelModal
                                    subscription={billingData?.subscription}
                                    setLoadingState={handleNestedLoadingState}
                                    onCloseModal={handleCloseModalsAndReload}
                                    setModalIsOpen={setCancelModalIsOpen}
                                    modalIsOpen={cancelModalIsOpen}
                                    billingData={billingData}
                                />
                            )}

                        <a
                            href="https://ghost.org/help/manage-your-subscription/?ref=billing.ghost.org"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Billing Help
                        </a>
                    </footer>
                </div>
            )}
        </main>
    );
}

Dashboard.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
        replace: PropTypes.func,
        location: PropTypes.object,
    }).isRequired,
    updateTitle: PropTypes.func.isRequired,
    showNav: PropTypes.func,
};

export default withRouter(Dashboard);
